import { Tour } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useTranslation from '../../localization/useTranslation';

export const ONBOARDING_STORAGE_KEY = 'dentrist-onboarding-completed';
export const ONBOARDING_RESTART_EVENT = 'dentrist:start-onboarding';

const waitForTargets = (getters, { attempts = 20, intervalMs = 150 } = {}) =>
  new Promise((resolve) => {
    let tries = 0;
    const tick = () => {
      const ready = getters.every((get) => {
        try {
          return Boolean(get());
        } catch {
          return false;
        }
      });
      if (ready || tries >= attempts) {
        resolve(ready);
        return;
      }
      tries += 1;
      window.setTimeout(tick, intervalMs);
    };
    tick();
  });

/**
 * Guided product tour for the vendor portal shell.
 * Auto-starts once for first-time authenticated users; can be restarted via NavBar.
 */
const OnboardingTour = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);

  const markCompleted = useCallback(() => {
    try {
      window.localStorage.setItem(ONBOARDING_STORAGE_KEY, '1');
    } catch {
      // Ignore storage failures.
    }
  }, []);

  const startTour = useCallback(async () => {
    const ready = await waitForTargets([
      () => document.getElementById('tour-sidebar'),
      () => document.getElementById('tour-header-controls'),
      () => document.getElementById('tour-navbar'),
      () => document.getElementById('tour-content'),
    ]);
    if (!ready) {
      return;
    }
    setCurrent(0);
    setOpen(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const maybeAutoStart = async () => {
      try {
        const done = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
        if (!done && !cancelled) {
          await startTour();
        }
      } catch {
        if (!cancelled) {
          await startTour();
        }
      }
    };

    maybeAutoStart();

    const onRestart = () => {
      startTour();
    };
    window.addEventListener(ONBOARDING_RESTART_EVENT, onRestart);

    return () => {
      cancelled = true;
      window.removeEventListener(ONBOARDING_RESTART_EVENT, onRestart);
    };
  }, [startTour]);

  const steps = useMemo(
    () => [
      {
        title: t('onboarding.welcome.title'),
        description: t('onboarding.welcome.description'),
        target: () => document.getElementById('tour-sidebar'),
        placement: 'right',
      },
      {
        title: t('onboarding.navigation.title'),
        description: t('onboarding.navigation.description'),
        target: () => document.querySelector('#tour-sidebar .sidebar-menu'),
        placement: 'right',
      },
      {
        title: t('onboarding.controls.title'),
        description: t('onboarding.controls.description'),
        target: () => document.getElementById('tour-header-controls'),
        placement: 'bottom',
      },
      {
        title: t('onboarding.account.title'),
        description: t('onboarding.account.description'),
        target: () => document.getElementById('tour-navbar'),
        placement: 'bottomLeft',
      },
      {
        title: t('onboarding.workspace.title'),
        description: t('onboarding.workspace.description'),
        target: () => document.getElementById('tour-content'),
        placement: 'top',
      },
    ],
    [t]
  );

  const handleClose = () => {
    setOpen(false);
    markCompleted();
  };

  return (
    <Tour
      open={open}
      current={current}
      onChange={setCurrent}
      onClose={handleClose}
      onFinish={handleClose}
      steps={steps}
      type="primary"
    />
  );
};

export const startOnboardingTour = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(ONBOARDING_RESTART_EVENT));
};

export default OnboardingTour;
